/**
 * Generato automaticamente.
 * OpenAPI
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  CategoriaDTO,
  CategoriaRequest,
  GetCategorieParams,
  WsProblema,
  WsRispostaPaginataListCategoriaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Aggiorna una categoria.
 * @summary Modifica Categoria
 */
export const putCategorieId = (
  id: string,
  categoriaRequest: CategoriaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<CategoriaDTO>(
    {
      url: `/framework-cfg-domande/categorie/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: categoriaRequest,
    },
    options,
  );
};

export const getPutCategorieIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putCategorieId>>,
    TError,
    { id: string; data: CategoriaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putCategorieId>>,
  TError,
  { id: string; data: CategoriaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putCategorieId>>,
    { id: string; data: CategoriaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putCategorieId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutCategorieIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putCategorieId>>
>;
export type PutCategorieIdMutationBody = CategoriaRequest;
export type PutCategorieIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Modifica Categoria
 */
export const usePutCategorieId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putCategorieId>>,
    TError,
    { id: string; data: CategoriaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutCategorieIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove il collegamento categoria-tipoDomanda e la elimina definitivamente.
 * @summary Elimina Categoria
 */
export const deleteCategorieId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-domande/categorie/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteCategorieIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCategorieId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCategorieId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCategorieId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteCategorieId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCategorieIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteCategorieId>>
>;

export type DeleteCategorieIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina Categoria
 */
export const useDeleteCategorieId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCategorieId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteCategorieIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco delle categorie
 * @summary Elenco Categorie
 */
export const getCategorie = (
  params?: GetCategorieParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListCategoriaDTO>(
    { url: `/framework-cfg-domande/categorie`, method: 'get', params, signal },
    options,
  );
};

export const getGetCategorieQueryKey = (params?: GetCategorieParams) =>
  [`/framework-cfg-domande/categorie`, ...(params ? [params] : [])] as const;

export const getGetCategorieQueryOptions = <
  TData = Awaited<ReturnType<typeof getCategorie>>,
  TError = ErrorType<unknown>,
>(
  params?: GetCategorieParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCategorie>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getCategorie>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCategorieQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCategorie>>> = ({
    signal,
  }) => getCategorie(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetCategorieQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCategorie>>
>;
export type GetCategorieQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Categorie
 */
export const useGetCategorie = <
  TData = Awaited<ReturnType<typeof getCategorie>>,
  TError = ErrorType<unknown>,
>(
  params?: GetCategorieParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCategorie>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetCategorieQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo categoria.
 * @summary Crea Categoria
 */
export const postCategorie = (
  categoriaRequest: CategoriaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<CategoriaDTO>(
    {
      url: `/framework-cfg-domande/categorie`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: categoriaRequest,
    },
    options,
  );
};

export const getPostCategorieMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postCategorie>>,
    TError,
    { data: CategoriaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postCategorie>>,
  TError,
  { data: CategoriaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postCategorie>>,
    { data: CategoriaRequest }
  > = props => {
    const { data } = props ?? {};

    return postCategorie(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostCategorieMutationResult = NonNullable<
  Awaited<ReturnType<typeof postCategorie>>
>;
export type PostCategorieMutationBody = CategoriaRequest;
export type PostCategorieMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Categoria
 */
export const usePostCategorie = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postCategorie>>,
    TError,
    { data: CategoriaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostCategorieMutationOptions(options);

  return useMutation(mutationOptions);
};
